'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useAuth } from '@/app/context/AuthContext';
import DesignCard from '@/app/designs/components/DesignCard';
import { DESIGN_TYPES, DESIGN_TYPE_LIST, designPath, type PublicDesign } from '@/app/lib/community/shared';
import { markPublishedSeen } from '@/app/lib/community/publishedUpdates';

const STATUS_LABEL: Record<PublicDesign['status'], { text: string; className: string }> = {
  approved: { text: 'Live', className: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  pending: { text: 'Waiting for review', className: 'bg-amber-500/15 text-amber-200 border-amber-500/30' },
  removed: { text: 'Removed by a moderator', className: 'bg-red-500/15 text-red-300 border-red-500/30' },
};

/**
 * My Library → Published: every design the user has shared, in every status. Lives next to the builds
 * it came from (it used to be /designs/mine, which nothing linked to). Opening it marks moderator
 * decisions as seen, which clears the dot on the sidebar's My Library link.
 */
export function PublishedDesigns() {
  const { user } = useAuth();
  const [designs, setDesigns] = useState<PublicDesign[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/designs/mine', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
        const data = await res.json();
        if (!res.ok) throw new Error(typeof data.error === 'string' ? data.error : 'Could not load your designs.');
        if (cancelled) return;
        const list = data.designs as PublicDesign[];
        setDesigns(list);
        markPublishedSeen(user.id, list);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load your designs.');
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const editors = DESIGN_TYPE_LIST.map(t => DESIGN_TYPES[t]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <p className="text-sm text-zinc-400 max-w-2xl">
          Designs you have shared with the community. Every one is checked by a moderator before it appears in the gallery.
        </p>
        <Link href="/designs" className="text-sm text-emerald-400 hover:text-emerald-300">Browse community designs →</Link>
      </div>

      {error && <p role="alert" className="text-sm text-red-400">{error}</p>}

      {designs === null && !error && (
        <div className="flex items-center gap-3 text-zinc-400 text-sm">
          <div className="w-5 h-5 border-2 border-zinc-600 border-t-emerald-500 rounded-full animate-spin" />
          Loading your designs…
        </div>
      )}

      {designs && designs.length === 0 && (
        <div className="bg-zinc-900/50 border border-zinc-800 border-dashed rounded-xl p-8 text-center text-sm text-zinc-400">
          You have not published any designs yet. Choose <span className="text-zinc-200">Publish to community</span> in the{' '}
          {editors.map((info, i) => (
            <span key={info.tool}>
              {i > 0 && (i === editors.length - 1 ? ' or ' : ', ')}
              <Link href={`/tools/${info.tool}`} className="text-emerald-400 hover:text-emerald-300">{info.editorName}</Link>
            </span>
          ))}
          , or open a saved design here and publish it from there.
        </div>
      )}

      {designs && designs.length > 0 && (
        // Same track size as the /designs gallery (150px): at 170px a phone got one ~450px-tall card per row.
        <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(150px,1fr))]">
          {designs.map(d => (
            <DesignCard
              key={d.id}
              design={d}
              footer={
                <div className="space-y-1.5">
                  <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full border ${STATUS_LABEL[d.status].className}`}>{STATUS_LABEL[d.status].text}</span>
                    <Link href={designPath(d.type, d.id)} className="text-xs text-zinc-400 hover:text-white">Manage</Link>
                  </div>
                  {d.status === 'removed' && d.moderationNote && (
                    <p className="text-[11px] text-red-300/80 break-words">Reason: {d.moderationNote}</p>
                  )}
                </div>
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}
